import { getBankColor } from '@/lib/bank-colors';
import { type PageRequest, rereadLoadedPages } from '@/lib/paging';
import type {
  BillingCyclePosition,
  CreditCardCockpitAccount,
  CreditCardCockpitResponse,
  CreditCardInvoice,
  InvoiceDetailResponse,
} from '@/lib/api';
import type { FaturaStatus } from '@/lib/transactions/types';
import type { AccountOption, ScreenFatura } from './types';

const ID_PREFIX = 'fatura';

function deriveStatus(invoice: CreditCardInvoice, now: Date): FaturaStatus {
  if (invoice.isPaid) return 'paid';
  if (!invoice.isOpen) {
    const due = invoice.dueDate ? new Date(invoice.dueDate) : null;
    if (due && due.getTime() < now.getTime()) return 'overdue';
    return 'closed';
  }
  return 'open';
}

function maskLast4(number: string | null | undefined): string {
  if (!number) return '';
  return number.replace(/\D/g, '').slice(-4);
}

/**
 * Adapter from the cockpit endpoint's `CreditCardInvoice` (legacy shape) to the
 * redesign-canonical `Fatura` (M1 contract), augmented with bank theming pulled
 * from the matching `AccountOption`. Bank color resolution flows through M1's
 * `getBankColor` chain.
 */
export function adaptInvoiceToFatura(
  invoice: CreditCardInvoice,
  account: CreditCardCockpitAccount,
  accountOption: AccountOption | undefined,
  now: Date = new Date(),
): ScreenFatura {
  const id = `${ID_PREFIX}:${account.id}:${invoice.period}:${invoice.position}`;
  const bank = accountOption?.bank ?? account.name;
  const last4 = accountOption?.last4 ?? maskLast4(account.number);
  const bankColor = getBankColor({
    connectorId: accountOption?.connectorId ?? account.connectorId ?? null,
    connectorPrimaryColor: accountOption?.connectorPrimaryColor ?? account.connectorPrimaryColor ?? null,
    colorOverride: accountOption?.colorOverride ?? account.colorOverride ?? null,
  });

  return {
    id,
    accountId: account.id,
    cycleStart: invoice.cycleStart ?? invoice.closeDate ?? invoice.dueDate ?? '',
    cycleEnd: invoice.cycleEnd ?? invoice.closeDate ?? invoice.dueDate ?? '',
    dueDate: invoice.dueDate ?? invoice.closeDate ?? '',
    cycleProgress: invoice.cycleProgress ?? (invoice.isOpen ? 0 : 1),
    status: deriveStatus(invoice, now),
    topCategoryIds: invoice.topCategoryIds ?? [],
    bank,
    bankColor,
    last4,
    label: accountOption?.label,
    creditBrand: accountOption?.creditBrand ?? account.creditBrand ?? null,
    connectorImage: accountOption?.connectorImage ?? account.image ?? null,
    creditLimit: account.creditLimit,
    creditAvailableLimit: account.creditAvailableLimit,
    dueDay: account.dueDay,
    installmentLadder: account.installmentLadder ?? [],
    total: invoice.total,
    transactionCount: invoice.transactionCount,
    isEstimated: invoice.isEstimated ?? false,
    estimatedCloseDate: invoice.estimatedCloseDate ?? null,
    paidAmount: invoice.paidAmount ?? null,
    paidAt: invoice.paidAt ?? null,
    source: invoice,
  };
}

/** Calendar (year, month) the invoice-detail endpoints address a fatura by. */
export function parseInvoiceDetailTarget(fatura: ScreenFatura): { year: number; month: number } {
  const ref = fatura.source.position === 'closing'
    ? fatura.source.dueDate ?? fatura.dueDate
    : fatura.source.period;
  if (!ref.includes('T')) {
    const [year, month] = ref.split('-').map(Number);
    return { year, month };
  }
  // UTC getters: local getters would shift a UTC-midnight due date on the 1st of a
  // month into the previous month on hosts west of UTC (wrong target → 404). Offset-less
  // datetimes (the backend's "o" serialization of unspecified kinds) are treated as UTC —
  // `new Date` would otherwise read them as browser-local and shift on UTC+ hosts.
  const dt = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(ref) ? ref : `${ref}Z`);
  return { year: dt.getUTCFullYear(), month: dt.getUTCMonth() + 1 };
}

/** Identity of one invoice inside the cockpit payload, for paid-state patching. */
export type InvoicePaidKey = {
  accountId: string;
  period: string;
  position: BillingCyclePosition;
};

export function toInvoicePaidKey(fatura: ScreenFatura): InvoicePaidKey {
  return {
    accountId: fatura.accountId,
    period: fatura.source.period,
    position: fatura.source.position,
  };
}

/**
 * Cockpit patch after a successful mark-as-paid: flips `isPaid` on the matching
 * invoice so the UI reflects the confirmation before the snapshot catches up.
 */
export function markInvoicePaidInCockpit(
  cockpit: CreditCardCockpitResponse,
  key: InvoicePaidKey,
): CreditCardCockpitResponse {
  return {
    ...cockpit,
    accounts: cockpit.accounts.map((account) => account.id !== key.accountId
      ? account
      : {
          ...account,
          invoices: account.invoices.map((invoice) =>
            invoice.period === key.period && invoice.position === key.position
              ? { ...invoice, isPaid: true }
              : invoice),
        }),
  };
}

export type InvoicePageRead = (page: PageRequest) => Promise<InvoiceDetailResponse>;

// Reading an open invoice's charges again (a space write changed their markers, VER-571): every charge it shows, in
// pages the API takes.
export function reloadInvoiceDetail(
  read: InvoicePageRead,
  current: InvoiceDetailResponse | null,
  pageSize: number,
): Promise<InvoiceDetailResponse> {
  return rereadLoadedPages(read, invoiceCharges, appendInvoiceDetailPage, current?.transactions.items.length ?? 0, pageSize);
}

function invoiceCharges(detail: InvoiceDetailResponse) {
  return detail.transactions;
}

// A reread lands on the invoice it was read for (the person may have opened another one meanwhile), or in place of
// none, when the invoice's first read failed.
export function invoiceAfterReread(
  current: InvoiceDetailResponse | null,
  reread: InvoiceDetailResponse,
): InvoiceDetailResponse | null {
  return !current || isSameInvoice(current, reread) ? reread : current;
}

export function isSameInvoice(a: InvoiceDetailResponse, b: InvoiceDetailResponse): boolean {
  return a.accountId === b.accountId && a.invoice.period === b.invoice.period && a.invoice.position === b.invoice.position;
}

// "Carregar mais": the next page's charges after the ones loaded, under the latest page's paging and header.
export function appendInvoiceDetailPage(
  current: InvoiceDetailResponse | null,
  next: InvoiceDetailResponse,
): InvoiceDetailResponse {
  if (!current) return next;
  return {
    ...next,
    transactions: { ...next.transactions, items: [...current.transactions.items, ...next.transactions.items] },
  };
}
